import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { UserType } from "../types/userType";

export interface UserAttribute {
  att_name: string;
  att_value: string | boolean;
}

export interface UserInfo {
  userId?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  userType?: number;
  is_access?: boolean;
  password?: string;
  org_id?: number;
  org_type?: number;
}

type ParsedAttributeValue = string | boolean | number;

interface UserState {
  userInfo: UserInfo;
  user_attributes: Record<string, ParsedAttributeValue>;
  justLoggedIn: boolean;

  setUserInfo: (info: UserInfo) => void;
  setAttributes: (user_attributes: UserAttribute[]) => void;
  updateAttribute: (name: string, value: string | boolean | number) => void;
  setJustLoggedIn: (val: boolean) => void;

  isAdminFlow: boolean;
  setAdminFlow: (val: boolean) => void;

  navigationReturnPath: string | null;
  setNavigationReturnPath: (path: string | null) => void;

  // Persisted login flag — set client-side on login/OTP success.
  // Using localStorage (via Zustand persist) instead of a cookie avoids
  // cross-origin issues on Azure where the SWA and API are on different domains.
  isLoggedIn: boolean;
  setLoggedIn: (val: boolean) => void;

  isOnboarded: () => boolean;

  getGradeLabel: () => string;
  getSchoolLabel: () => string;
  getUnitsLabel: () => string;
  isProfileComplete: () => boolean;

  getUserType: () => string | null;
  isAdmin: () => boolean;
  isTeacher: () => boolean;
  isRegularStudent: () => boolean;
  isSchoolStudent: () => boolean;
  isSchoolUserNoAccess: () => boolean;
  isAccessGranted: () => boolean;
  isAuthenticated: () => boolean;

  resetUser: () => void;
}

// Utility function to parse attribute values
const parseValue = (value: string | boolean | number): ParsedAttributeValue => {
  if (value === "true") return true;
  if (value === "false") return false;
  if (typeof value === "string" && !isNaN(Number(value))) return Number(value);
  return value;
};

export const useUserStore = create(
  persist<UserState>(
    (set, get) => ({
      userInfo: {},
      user_attributes: {},
      justLoggedIn: false,
      isLoggedIn: false,
      isAdminFlow: false,
      navigationReturnPath: null,


      setUserInfo: (info) =>
        set((state) => ({
          userInfo: {
            ...state.userInfo,
            ...info,
          },
        })),

      setAttributes: (user_attributes) => {
        const flat: Record<string, ParsedAttributeValue> = {};
        if (Array.isArray(user_attributes)) {
          user_attributes.forEach(attr => {
            flat[attr.att_name] = parseValue(attr.att_value);
          });
        }
        set({ user_attributes: flat });
      },


      updateAttribute: (name, value) => {
        const parsed = parseValue(value);
        set((state) => ({
          user_attributes: {
            ...state.user_attributes,
            [name]: parsed,
          },
        }));
      },

      isOnboarded: () => {
        const val = get().user_attributes["IsOnboarded"];
        return val === true || val === "true" || val === 1 || val === "1";
      },


      setJustLoggedIn: (val: boolean) => set({ justLoggedIn: val }),

      setLoggedIn: (val: boolean) => set({ isLoggedIn: val }),

      setAdminFlow: (val: boolean) => set({ isAdminFlow: val }),

      setNavigationReturnPath: (path: string | null) => set({ navigationReturnPath: path }),

      getUserType: () => {
        const userType = get().user_attributes["user_type"] ?? get().userInfo?.userType;
        return typeof userType === "string" ? userType : userType?.toString() ?? null;
      },

      isAdmin: () => Number(get().getUserType()) === UserType.ADMIN,
      isTeacher: () => Number(get().getUserType()) === UserType.TEACHER,
      isRegularStudent: () => Number(get().getUserType()) === UserType.REGULAR_USER,
      isSchoolStudent: () => Number(get().getUserType()) === UserType.SCHOOL_USER,

      getGradeLabel: () => String(get().user_attributes["grade_label"] || ""),
      getSchoolLabel: () => String(get().user_attributes["school_label"] || ""),
      getUnitsLabel: () => String(get().user_attributes["units_label"] || ""),

      isProfileComplete: () => {
        const { userInfo, user_attributes } = get();
        const hasGender = !!user_attributes["gender"];
        const hasClass = !!user_attributes["rel_classes"];
        const hasLevel = !!user_attributes["rel_exercise_levels"];

        if (!hasGender || !hasClass || !hasLevel) {
          return false;
        }

        if (get().isSchoolStudent()) {
          const hasValidSchoolOrg = !!userInfo?.org_id && userInfo?.org_type !== 1;
          const rawTeachers = user_attributes["rel_teachers"];
          let hasTeacher = false;
          if (Array.isArray(rawTeachers)) {
            hasTeacher = rawTeachers.length > 0;
          } else if (typeof rawTeachers === "string" && rawTeachers.trim() !== "") {
            try {
              const parsed = JSON.parse(rawTeachers);
              hasTeacher = Array.isArray(parsed) ? parsed.length > 0 : !!parsed;
            } catch {
              hasTeacher = rawTeachers !== "[]";
            }
          } else if (typeof rawTeachers === "number") {
            hasTeacher = true;
          }
          return hasValidSchoolOrg && hasTeacher;
        }

        return true;
      },

      isSchoolUserNoAccess: () => {
        const { userInfo } = get();
        return !userInfo.is_access && get().isSchoolStudent() && userInfo.org_type === 1;
      },

      isAccessGranted: () => {
        // KAN-322: Bypass subscription validation for Shoonit V2
        return true;
      },

      isAuthenticated: () => get().isLoggedIn,

      resetUser: () => {
        localStorage.removeItem("shoonit_cookies_working");
        set({
          userInfo: {},
          user_attributes: {},
          isLoggedIn: false,
          isAdminFlow: false,
          navigationReturnPath: null
        });
      },
    }),
    {
      name: "shoonit-users",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
